import { ReactComponent as SortDescIcon } from '../../assets/icons/arrow-down-s-fill.svg';
import { ReactComponent as SortableIcon } from '../../assets/icons/arrow-down-s-unfill.svg';
import { ReactComponent as SortAscIcon } from '../../assets/icons/arrow-up-s-fill.svg';

import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { getFlagSvg } from '../../Components/Common/Flag/Flag';
import SvgImage from '../../Components/Common/SvgImage/SvgImage';
import { AC_TYPES, AC_UNITS } from '../../constants/screener/AC.types.constant';
import { TableMode } from '../../constants/screener/screener.constant';
import {
  Criteria,
  ScreenerTableColumnType,
  SortingType,
} from '../../types/screener.type';
import { formatNumber } from '../../utils/utils';

import { Tooltip as MuiTooltip, TooltipProps } from '@mui/material';
import React, { useEffect, useState } from 'react';
import Dropdown from '../../Components/Common/Dropdown/Dropdown';
import { DropdownItemType } from '../../types/common.type';

import { apiBase } from '../../api/apiBase';
const formatTimePeriod = (value: string) => value.toUpperCase();

const ScrollCloseTooltip = (props: TooltipProps) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (open) setOpen(false);
    };
    // Capture scrolls anywhere in the document
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  return (
    <MuiTooltip
      {...props}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
    />
  );
};
const ArrowSortingIndicator = ({ sorting }: { sorting: string }) => {
  const Icon =
    sorting === 'asc' ? SortAscIcon : sorting ? SortDescIcon : SortableIcon;

  return (
    <div className="screener-table-sorting">
      <Icon
        className={
          sorting
            ? 'screener-table-sorting-desc-icon active'
            : 'screener-table-sorting-icon-sortable'
        }
      />
    </div>
  );
};

interface UseScreenerColumnProps {
  mode: TableMode;
  selectedCriteria: Criteria[];
  allCriteria?: Criteria[];
  region: string;
  page: number;
  size: number;
  sorting: SortingType;
  setSorting: (sorting: SortingType) => void;
  token: string;
  columnVisibility?: { [key: string]: boolean };
  setColumnVisibility?: (visibility: { [key: string]: boolean }) => void;
  isSmallScreen?: boolean;
  tooltipRef: React.RefObject<HTMLDivElement>;
  setTooltip: (tooltip: any) => void;
  onStockClick?: (symbol: string) => void;
}

const useScreenerColumn = ({
  mode,
  selectedCriteria,
  allCriteria,
  region,
  page,
  size,
  sorting,
  setSorting,
  token,
  columnVisibility,
  setColumnVisibility,
  isSmallScreen,
  setTooltip,
  tooltipRef,
  onStockClick,
}: UseScreenerColumnProps) => {
  useEffect(() => {
    const handleScroll = () => {
      setTooltip((prev: any) => ({ ...prev, show: false }));
    };
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [setTooltip]);

  const dp = region.includes('US') ? 4 : 3;

  const getIndexChart = (ric: string) => {
    return `${apiBase()}/chart/?int=2&sym=${ric}&style=1&per=1&token=${encodeURIComponent(token || '')}`;
  };

  const handleSorting = useCallback(
    (id: string) => {
      let sort: SortingType = { id: '', option: '', asc: false };
      sort = { ...sorting };
      const sortItem = selectedCriteria.find((adv) => adv.id === id);

      // Any criterion carrying a timeValue (whether it's a selectable
      // "type: time" criterion, or a period baked straight into its id like
      // the PV-70_* price-change family) must forward that value as `option`
      // so the backend can disambiguate which period to sort by - mirrors
      // the same `option: item.timeValue` rule used for the advanced filter.
      const sortOption = sortItem?.timeValue || undefined;

      if (sortItem) {
        if (sorting.option === sortOption && id === sorting.id) {
          sort.asc = !sort.asc;
        } else {
          sort.id = id;
          sort.option = sortOption;
          sort.asc = false;
        }
      } else {
        if (sorting.id === id) {
          sort.asc = !sort.asc;
        } else {
          sort.id = id;
          sort.asc = false;
          sort.option = undefined;
        }
      }
      setSorting(sort);
    },
    [sorting, setSorting, selectedCriteria],
  );

  const detectSorting = (id: string) => {
    const crit = selectedCriteria.find((adv: any) => adv.id === id);

    const idMatch = sorting.id === id;
    const optionMatch = crit?.timeValue
      ? sorting.option === crit.timeValue
      : true;

    if (idMatch && optionMatch && sorting.asc) {
      return 'asc';
    } else if (idMatch && optionMatch && !sorting.asc) {
      return 'desc';
    } else return '';
  };

  const basicColumnNormal: ScreenerTableColumnType[] = useMemo(() => {
    return [
      {
        id: 'index',
        accessorKey: 'index',
        label: '#',
        cellClassName: 'screener-table-index',
        headerClassName:
          'screener-table-header-index screener-table-header-border',
        renderHeader: () => {
          return <div className="screener-table-header-index-container">#</div>;
        },
        renderCell: (row, index) => {
          return (page - 1) * size + index + 1;
        },
      },
      {
        id: 'country',
        accessorKey: 'cc',
        label: 'Ctry',
        cellClassName: 'screener-table-country',
        headerClassName:
          'screener-table-header-country screener-table-header-border',
        renderHeader: () => {
          return '';
        },
        renderCell: (row) => {
          return (
            <div className="screener-table-cell-country">
              {getFlagSvg(row.cc)}
            </div>
          );
        },
      },
      {
        id: 'ticker',
        accessorKey: 'ticker',
        label: 'Code',
        cellClassName: 'screener-table-ticker',
        headerClassName:
          'screener-table-header-ticker screener-table-header-border',
        renderHeader: (id: string) => {
          return (
            <div className="screener-table-header-ticker-container">
              <div
                className="screener-table-header-ticker-wrapper"
                onClick={() => handleSorting(id)}
              >
                <div className="screener-table-header-ticker-title">Code</div>
                <ArrowSortingIndicator sorting={detectSorting(id)} />
              </div>
            </div>
          );
        },
        renderCell: (row) => {
          return (
            <div
              className="screener-table-cell-ticker"
              onClick={() => onStockClick?.(row.bell_code)}
            >
              {row.ticker}
            </div>
          );
        },
      },
      {
        id: 'name',
        accessorKey: 'name',
        label: 'Name',
        cellClassName: 'screener-table-name',
        headerClassName:
          'screener-table-header-name screener-table-header-border',
        renderHeader: (id: string) => {
          return (
            <div className="screener-table-header-name-container">
              <div
                className="screener-table-header-name-wrapper"
                onClick={() => handleSorting(id)}
              >
                <div className="screener-table-header-name-title">Name</div>
                <ArrowSortingIndicator sorting={detectSorting(id)} />
              </div>
            </div>
          );
        },
        renderCell: (row) => {
          const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
            const target = e.currentTarget;
            const rect = target.getBoundingClientRect();
            const scrollX =
              window.pageXOffset || document.documentElement.scrollLeft;
            const scrollY =
              window.pageYOffset || document.documentElement.scrollTop;

            const x = rect.left + scrollX;
            const y = rect.bottom + 2 + scrollY;

            setTooltip({
              content: row.name,
              show: true,
              x,
              y,
            });
          };

          const handleMouseLeave = () => {
            setTooltip((prev: any) => ({ ...prev, show: false }));
          };

          return (
            <div
              className="screener-table-cell-name"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              dangerouslySetInnerHTML={{ __html: row.name }}
            />
          );
        },
      },
      {
        id: 'today',
        accessorKey: 'today',
        label: 'Today',
        cellClassName: 'screener-table-today',
        headerClassName:
          'screener-table-header-today screener-table-header-border',
        renderHeader: () => {
          return <div className="screener-table-header-today-title">Today</div>;
        },
        renderCell: (row) => {
          return (
            <div className="screener-table-today-chart">
              <SvgImage svgApiUrl={getIndexChart(row.ric)} />
            </div>
          );
        },
      },
      {
        id: 'last',
        accessorKey: 'last',
        label: 'Last price',
        cellClassName: 'screener-table-last',
        headerClassName:
          'screener-table-header-last screener-table-header-border',
        renderHeader: (id: string) => {
          return (
            <div
              className="screener-table-header-last-container"
              onClick={() => handleSorting(id)}
            >
              <div className="screener-compact-table-header-last-title">
                Last price
              </div>
              <ArrowSortingIndicator sorting={detectSorting(id)} />
            </div>
          );
        },
        renderCell: (row) => {
          return formatNumber(row.last, row.dp, row.dp);
        },
      },
      {
        id: 'ccy',
        accessorKey: 'ccy',
        label: 'Ccy',
        cellClassName: 'screener-table-ccy',
        headerClassName:
          'screener-table-header-ccy screener-table-header-border',
        renderHeader: (id: string) => {
          return (
            <div
              className="screener-table-header-ccy-container"
              onClick={() => handleSorting(id)}
            >
              Ccy
              <ArrowSortingIndicator sorting={detectSorting(id)} />
            </div>
          );
        },
        renderCell: (row) => {
          return row.currency;
        },
      },
      {
        id: 'pc',
        accessorKey: 'pc',
        label: 'Per chg',
        cellClassName: 'screener-table-pc',
        headerClassName:
          'screener-table-header-pc screener-table-header-border',
        renderHeader: (id: string) => {
          return (
            <div
              className="screener-table-header-pc-container"
              onClick={() => handleSorting(id)}
            >
              <div className="screener-table-header-pc-title">%Chg</div>
              <ArrowSortingIndicator sorting={detectSorting(id)} />
            </div>
          );
        },
        renderCell: (row) => {
          return (
            <div
              className={classNames('screener-table-cell-pc', {
                positive: Number(row.pctchng) > 0,
                negative: Number(row.pctchng) < 0,
              })}
            >{`${row.pctchng !== '-' ? `${row.pctchng}%` : '-'}`}</div>
          );
        },
      },
    ];
  }, [sorting, page, size, onStockClick, dp]);

  const basicColumnCompact: ScreenerTableColumnType[] = useMemo(() => {
    return [
      {
        id: 'index',
        accessorKey: 'index',
        label: '#',
        cellClassName: 'screener-table-index',
        headerClassName: 'screener-table-header-index',
        renderHeader: () => {
          return '#';
        },
        renderCell: (row, index) => {
          return (page - 1) * size + index + 1;
        },
      },
      {
        id: 'country',
        accessorKey: 'cc',
        label: 'Ctry',
        cellClassName: 'screener-table-country',
        headerClassName: 'screener-table-header-country',
        renderHeader: () => {
          return '';
        },
        renderCell: (row) => {
          return (
            <div className="screener-table-cell-country">
              {getFlagSvg(row.cc)}
            </div>
          );
        },
      },
      {
        id: 'name',
        accessorKey: 'name',
        label: 'Name',
        cellClassName: 'screener-compact-table-name',
        headerClassName: 'screener-compact-table-header-name',
        renderHeader: () => {
          return 'Name';
        },
        renderCell: (row) => {
          return (
            <div className="screener-compact-table-cell-name-container">
              <div
                className="screener-compact-table-cell-ticker"
                onClick={() => onStockClick?.(row.bell_code)}
              >
                {row.ticker}
              </div>
              <div className="screener-compact-table-cell-name">{row.name}</div>
            </div>
          );
        },
      },
      {
        id: 'today',
        accessorKey: 'today',
        label: 'Today',
        cellClassName: 'screener-compact-table-today',
        headerClassName: 'screener-compact-table-header-today',
        renderHeader: () => {
          return (
            <div className="screener-compact-table-header-today-title">
              Today
            </div>
          );
        },
        renderCell: (row) => {
          return (
            <div className="screener-compact-table-today-chart">
              <SvgImage svgApiUrl={getIndexChart(row.ric)} />
            </div>
          );
        },
      },
      {
        id: 'last',
        accessorKey: 'last',
        label: 'Last price',
        cellClassName: 'screener-compact-table-last',
        headerClassName: 'screener-compact-table-header-last',
        renderHeader: (header: any) => {
          return (
            <div
              className="screener-compact-table-header-last-container"
              onClick={() => handleSorting(header)}
            >
              <div className="screener-compact-table-header-last-title">
                Last price
              </div>
              <ArrowSortingIndicator sorting={detectSorting(header)} />
            </div>
          );
        },
        renderCell: (row: any) => {
          return (
            <div className="screener-compact-table-cell-last-container">
              <div className="screener-compact-table-cell-last">
                {formatNumber(row.last, row.dp, row.dp)}
              </div>
              <div className="screener-compact-table-cell-last-ccy">
                {row.ccy}
              </div>
            </div>
          );
        },
      },
      {
        id: 'pc',
        accessorKey: 'pctchng',
        label: 'Last price',
        cellClassName: 'screener-compact-table-pc',
        headerClassName: 'screener-compact-table-header-pc',
        renderHeader: (id: string) => {
          return (
            <div
              className="screener-compact-table-header-pc-container"
              onClick={() => handleSorting(id)}
            >
              <div className="screener-compact-table-header-pc-title">%Chg</div>
              <ArrowSortingIndicator sorting={detectSorting(id)} />
              <div className="screener-compact-table-header-nc-title">Chg</div>
            </div>
          );
        },
        renderCell: (row) => {
          return (
            <div className="screener-compact-table-cell-pc-container">
              <div
                className={classNames('screener-compact-table-cell-pc', {
                  positive: Number(row.pctchng) > 0,
                  negative: Number(row.pctchng) < 0,
                })}
              >
                {formatNumber(row.pctchng, 2, 2, false, true, false, '%')}
              </div>
              <div className="screener-compact-table-cell-nc">
                {formatNumber(row.netchng, row.dp, row.dp)}
              </div>
            </div>
          );
        },
      },
    ];
  }, [
    page,
    size,
    onStockClick,
    getIndexChart,
    detectSorting,
    handleSorting,
    dp,
  ]);

  const basicColumnSmallScreen: ScreenerTableColumnType[] = useMemo(() => {
    return [
      {
        id: 'ticker',
        accessorKey: 'ticker',
        label: 'Code',
        colSpan: 2,
        cellClassName: 'screener-small-screen-table-name',
        headerClassName: 'screener-small-screen-table-header-ticker',
        renderHeader: (id: string) => {
          return (
            <div
              className="screener-small-screen-table-header-name-wrapper"
              onClick={() => handleSorting(id)}
            >
              <div className="screener-small-screen-table-header-ticker-title">
                Code
              </div>
              <ArrowSortingIndicator sorting={detectSorting(id)} />
            </div>
          );
        },
        renderCell: (row) => {
          const handleClickName = (e: React.MouseEvent<HTMLDivElement>) => {
            const target = e.currentTarget;
            const rect = target.getBoundingClientRect();
            const scrollX =
              window.pageXOffset || document.documentElement.scrollLeft;
            const scrollY =
              window.pageYOffset || document.documentElement.scrollTop;

            // Trên màn nhỏ, tooltip sát ngay dưới tên công ty
            const x = rect.left + scrollX;
            const y = rect.bottom + 2 + scrollY; // giảm offset xuống 2px

            setTooltip({
              content: row.name,
              show: true,
              x,
              y,
            });
          };

          return (
            <div className="screener-small-screen-table-cell-name-container">
              <div className="screener-small-screen-table-cell-name-icon">
                {getFlagSvg(row.cc)}
              </div>
              <div className="screener-small-screen-table-cell-name-content">
                <div
                  className="screener-small-screen-table-cell-ticker"
                  onClick={() => onStockClick?.(row.bell_code)}
                >
                  {row.ticker}
                </div>
                <div
                  className="screener-small-screen-table-cell-name"
                  onClick={handleClickName}
                  dangerouslySetInnerHTML={{ __html: row.name }}
                />
              </div>
            </div>
          );
        },
      },
      {
        id: 'name',
        accessorKey: 'name',
        label: 'Name',
        skipCell: true,
        cellClassName: 'screener-small-screen-table-name-spacer',
        headerClassName: 'screener-small-screen-table-header-name',
        renderHeader: (id: string) => {
          return (
            <div
              className="screener-small-screen-table-header-name-wrapper"
              onClick={() => handleSorting(id)}
            >
              <div className="screener-small-screen-table-header-name-title">
                Name
              </div>
              <ArrowSortingIndicator sorting={detectSorting(id)} />
            </div>
          );
        },
        renderCell: () => null,
      },
      {
        id: 'today',
        accessorKey: 'today',
        label: 'Today',
        cellClassName: 'screener-small-screen-table-today',
        headerClassName: 'screener-small-screen-table-header-today',
        renderHeader: () => {
          return (
            <div className="screener-small-screen-table-header-today-title">
              Today
            </div>
          );
        },
        renderCell: (row) => {
          return (
            <div className="screener-small-screen-table-today-chart">
              <SvgImage svgApiUrl={getIndexChart(row.ric)} />
            </div>
          );
        },
      },
      {
        id: 'last',
        accessorKey: 'last',
        label: 'Last',
        cellClassName: 'screener-small-screen-table-last',
        headerClassName: 'screener-small-screen-table-header-last',
        renderHeader: (id: string) => {
          return (
            <div
              className="screener-small-screen-table-header-last-container"
              onClick={() => handleSorting(id)}
            >
              Last
              <ArrowSortingIndicator sorting={detectSorting(id)} />
            </div>
          );
        },
        renderCell: (row) => {
          return (
            <div className="screener-small-screen-table-cell-last-container">
              <div className="screener-small-screen-table-cell-last">
                {formatNumber(row.last, row.dp, row.dp)}
              </div>
              {/* <div className={classNames("screener-small-screen-table-cell-pc", {
                            positive: Number(row.pctchng) > 0,
                            negative: Number(row.pctchng) < 0
                        })}>{formatNumber(row.pctchng, 2, 2, false, true, false, "%")}</div> */}
            </div>
          );
        },
      },
      {
        id: 'ccy',
        accessorKey: 'ccy',
        label: 'Ccy',
        cellClassName: 'screener-small-screen-table-ccy',
        headerClassName: 'screener-small-screen-table-header-ccy',
        renderHeader: (id: string) => {
          return (
            <div
              className="screener-small-screen-table-header-ccy-container"
              onClick={() => handleSorting(id)}
            >
              Ccy
              <ArrowSortingIndicator sorting={detectSorting(id)} />
            </div>
          );
        },
        renderCell: (row) => {
          return (
            <div className="screener-small-screen-table-cell-ccy">
              {row.currency}
            </div>
          );
        },
      },
      {
        id: 'pc',
        accessorKey: 'pc',
        label: 'Per chg',
        cellClassName: 'screener-small-screen-table-pc',
        headerClassName: 'screener-small-screen-table-header-pc',
        renderHeader: (id: string) => {
          return (
            <div
              className="screener-small-screen-table-header-pc-container"
              onClick={() => handleSorting(id)}
            >
              <div className="screener-small-screen-table-header-pc-title">
                %Chg
              </div>
              <ArrowSortingIndicator sorting={detectSorting(id)} />
            </div>
          );
        },
        renderCell: (row) => {
          return (
            <div
              className={classNames('screener-small-screen-table-cell-pc', {
                positive: Number(row.pctchng) > 0,
                negative: Number(row.pctchng) < 0,
              })}
            >{`${row.pctchng !== '-' ? `${row.pctchng}%` : '-'}`}</div>
          );
        },
      },
    ];
  }, [
    detectSorting,
    handleSorting,
    setTooltip,
    onStockClick,
    getIndexChart,
    dp,
  ]);

  // Column for selecting which advanced criteria to show (only on small screens)
  const criteriaSelectorColumn: ScreenerTableColumnType | null = useMemo(() => {
    // Only show on small screens
    if (!isSmallScreen) {
      return null;
    }

    if (!allCriteria || !setColumnVisibility || !columnVisibility) {
      return null;
    }

    const selectedCriteriaList = allCriteria.filter((c) => c.selected);
    if (selectedCriteriaList.length === 0) {
      return null;
    }

    const dropdownOptions: DropdownItemType[] = selectedCriteriaList.map(
      (c) => ({
        value: c.id,
        label:
          c.type === AC_TYPES.time ? `${c.label} (${c.timeValue})` : c.label,
        icon: undefined,
      }),
    );

    // Find the currently visible criterion index (only one should be visible)
    const currentlyVisibleIndex = selectedCriteriaList.findIndex(
      (c) => columnVisibility[c.id] !== false,
    );
    const initialValue =
      currentlyVisibleIndex !== -1 ? currentlyVisibleIndex : 0;
    const currentlyVisibleCriterion =
      currentlyVisibleIndex !== -1
        ? selectedCriteriaList[currentlyVisibleIndex]
        : selectedCriteriaList[0];

    const handleSelectionChange = (selectedIndex: number) => {
      if (!setColumnVisibility || !columnVisibility) return;

      const newVisibility = { ...columnVisibility };

      // First, set all selected criteria to false
      selectedCriteriaList.forEach((c) => {
        newVisibility[c.id] = false;
      });

      // Then, set only the selected one to true
      const selectedCriterion = selectedCriteriaList[selectedIndex];
      if (selectedCriterion) {
        newVisibility[selectedCriterion.id] = true;
      }

      // Always keep the criteria-selector column visible
      newVisibility['criteria-selector'] = true;

      setColumnVisibility(newVisibility);
    };

    return {
      id: 'criteria-selector',
      accessorKey: 'criteria-selector',
      label: 'Select criteria',
      cellClassName: 'screener-table-criteria-selector',
      headerClassName: 'screener-table-header-criteria-selector',
      renderHeader: () => {
        return (
          <Dropdown
            options={dropdownOptions}
            initialValue={initialValue}
            onSelect={handleSelectionChange}
          />
        );
      },
      renderCell: (row: any) => {
        // Find the currently visible criterion to display its value
        const visibleCriterion =
          selectedCriteriaList.find((c) => columnVisibility[c.id] !== false) ||
          currentlyVisibleCriterion;

        if (!visibleCriterion) {
          return null;
        }

        const value = row[visibleCriterion.id];

        // Use the same formatting logic as advanced columns
        if (visibleCriterion.type === AC_TYPES.score) {
          const scoreVal = parseFloat(value);
          return (
            <div className="screener-table-advanced-score">
              {!isNaN(scoreVal) ? Math.round(scoreVal) : '-'}
            </div>
          );
        }

        return value;
      },
    };
  }, [allCriteria, columnVisibility, setColumnVisibility, isSmallScreen]);

  const advancedColumn: ScreenerTableColumnType[] = useMemo(() => {
    const advancedColumns = selectedCriteria.map((item: Criteria) => {
      return {
        id: item.id,
        accessorKey: item.id,
        label: item.label,
        cellClassName: 'screener-table-advanced',
        headerClassName:
          'screener-table-header-advanced screener-table-header-border',
        renderHeader: (id: string) => {
          const unitSuffix =
            item.unit &&
            item.unit !== AC_UNITS.currency &&
            !item.label.includes(`(${item.unit})`)
              ? ` (${item.unit})`
              : '';
          const fullLabel =
            item.type === AC_TYPES.time
              ? `${item.label}${unitSuffix} (${formatTimePeriod(item.timeValue)})`
              : `${item.label}${unitSuffix}`;
          const shortLabel = item.shortLabel || item.label;
          const displayLabel =
            item.type === AC_TYPES.time
              ? `${shortLabel} (${formatTimePeriod(item.timeValue)})`
              : shortLabel;
          return (
            <div className="screener-table-header-advanced-container">
              <div
                className="screener-table-header-advanced-wrapper"
                onClick={() => handleSorting(id)}
              >
                <ScrollCloseTooltip
                  title={fullLabel}
                  arrow
                  enterTouchDelay={500}
                >
                  <div className="screener-table-header-advanced-title">
                    <span className="screener-table-header-advanced-label">
                      {displayLabel}
                    </span>
                    <ArrowSortingIndicator sorting={detectSorting(id)} />
                  </div>
                </ScrollCloseTooltip>
              </div>
            </div>
          );
        },
        renderCell: (row: any) => {
          const rowKey =
            item.type === AC_TYPES.time
              ? `${item.id}_${item.timeValue}`
              : item.id;
          if (item.type === AC_TYPES.score) {
            return (
              <div className="screener-table-advanced-score">
                {!isNaN(parseFloat(row[rowKey]))
                  ? Math.round(parseFloat(row[rowKey]))
                  : '-'}
              </div>
            );
          }
          return (
            <div className="screener-table-advanced-cell-container">
              <div className="screener-table-advanced-cell-value">
                {row[rowKey]}
              </div>
              {item.unit && item.unit === AC_UNITS.currency && (
                <div className="screener-table-advanced-cell-unit">
                  {region.includes('US') ? 'USD' : row.currency}
                </div>
              )}
            </div>
          );
        },
      };
    });
    return advancedColumns;
  }, [detectSorting, handleSorting, region, selectedCriteria]);

  const baseColumns =
    mode === TableMode.NORMAL ? basicColumnNormal : basicColumnCompact;
  const columnsWithSelector = isSmallScreen
    ? // ? [...basicColumnSmallScreen, ...(criteriaSelectorColumn ? [criteriaSelectorColumn] : [])]
      // ? [...basicColumnSmallScreen]
      [...basicColumnSmallScreen, ...advancedColumn]
    : [...baseColumns, ...advancedColumn];

  return columnsWithSelector;
};

export default useScreenerColumn;
